import {
	SOCIAL_AUTH_END_POINT,
	SOCIAL_AUTH_VERIFY_END_POINT,
	SOCIAL_AUTH_RESEND_END_POINT,
} from '../auth.api';
import { HTTP_STATUS_CODE } from '../../../enums/http';
import { ROUTE_NAME } from '../../../enums/route';

let storeInstance = null;
let routerInstance = null;


export function setStore(store) {
	storeInstance = store;
}

export function setRouter(router) {
	routerInstance = router;
}

// Requests owned by the Two-factor Authentication page. Their failures are
// rendered by that page (inline under the input, or its own toast), so the
// global handler must not navigate away or stack a duplicate toast on top.
const TWO_FACTOR_END_POINTS = [
	SOCIAL_AUTH_VERIFY_END_POINT,
	SOCIAL_AUTH_RESEND_END_POINT,
];

const isTwoFactorEndpoint = (error) => {
	const url = error?.response?.config?.url || '';
	return TWO_FACTOR_END_POINTS.some((endPoint) => url.endsWith(endPoint));
};

export const interceptorResponse = (response) => {
	return response;
};

export const interceptorResponseError = async (error) => {
	// Network errors (offline, DNS failure, timeout, request canceled) have no
	// `response`. Bail out early so we don't crash on destructuring and don't
	// trigger router redirects that cause auth/profile loops.
	if (!error.response) {
		return Promise.reject(error);
	}

	const { response: { status: statusCode, data } } = error;

	// When responseType is 'blob', error body comes as a Blob instead of JSON
	// Parse it back to JSON so we can read error codes
	let errorData = data;
	if (data instanceof Blob) {
		try {
			const text = await data.text();
			errorData = JSON.parse(text);
		} catch {
			errorData = {};
		}
	}

	// Normalize errorData to always be a plain object
	// Handles empty response body cases where data is null, "", or undefined
	if (!errorData || typeof errorData !== 'object') {
		errorData = {};
	}

	// The 2FA page shows these errors itself - stay on the page and stay quiet.
	const isTwoFactorSelfHandled = isTwoFactorEndpoint(error);

	switch (statusCode) {
		// 400 - Bad Request
		case HTTP_STATUS_CODE.BAD_REQUEST:
			if (isTwoFactorSelfHandled) {
				break;
			}

			storeInstance.dispatch('toast/showToast', {
				header: 'Bad Request (400)',
				content: errorData?.message || 'There was a problem with your request.',
				type: 'danger',
			});
			break;

		// 401 - Unauthorized
		case HTTP_STATUS_CODE.UNAUTHORIZED:
			if (routerInstance.currentRoute.path !== '/logout') {
				routerInstance.push({ name: ROUTE_NAME.LOGOUT });
			}
			break;

		// 403 - Forbidden
		case HTTP_STATUS_CODE.FORBIDDEN:
			if (errorData.error === '2fa_required') {
				storeInstance.dispatch('auth/setTwoFactorError', {
					message: errorData.message,
					redirect: routerInstance.currentRoute.path,
				});

				if (routerInstance.currentRoute.path !== '/two-factor-authentication') {
					routerInstance.push({ name: ROUTE_NAME.TWO_FACTOR_AUTHENTICATION });
				}
			} else if (isTwoFactorSelfHandled) {
				// Verify / resend rejected (wrong or expired code, locked out).
				// Handled inline by the 2FA page - do NOT bounce to /forbidden,
				// which would unmount the page and lose the entered code.
				break;
			} else if (errorData.error === 'order_visibility_forbidden') {
				storeInstance.dispatch('toast/showToast', {
					header: 'ไม่สามารถเข้าถึง Order ได้',
					content: errorData.message,
					type: 'danger',
				});
				// eslint-disable-next-line require-atomic-updates
				error._toastShown = true;
				routerInstance.push({ name: ROUTE_NAME.FORBIDDEN });
			} else if (errorData.code === 'unauthorized_email_domain') {
				storeInstance.dispatch('toast/showToast', {
					header: 'ไม่สามารถ Export ได้',
					content: errorData.message,
					type: 'danger',
				});
				// eslint-disable-next-line require-atomic-updates
				error._toastShown = true;
			} else if (error.response.config.url !== SOCIAL_AUTH_END_POINT) {
				storeInstance.dispatch('toast/showToast', {
					header: 'Forbidden (403)',
					content: errorData?.message || 'You do not have permission to perform this action.',
					type: 'danger',
				});
				routerInstance.push({ name: ROUTE_NAME.FORBIDDEN });
			}
			break;

		// 404 - Not Found
		case HTTP_STATUS_CODE.NOT_FOUND:
			storeInstance.dispatch('toast/showToast', {
				header: 'Not Found (404)',
				content: errorData?.message || 'The requested resource could not be found.',
				type: 'danger',
			});
			break;

		// 405 - Method Not Allowed
		case HTTP_STATUS_CODE.METHOD_NOT_ALLOWED:
			storeInstance.dispatch('toast/showToast', {
				header: 'Method Not Allowed (405)',
				content: errorData?.message || 'Request method not allowed.',
				type: 'danger',
			});
			break;

		// 409 - Conflict
		case HTTP_STATUS_CODE.CONFLICT:
			storeInstance.dispatch('toast/showToast', {
				header: 'Conflict (409)',
				content: errorData?.message || 'Conflict occurred.',
				type: 'danger',
			});
			break;

		// 422 - Unprocessable Entity
		case HTTP_STATUS_CODE.UNPROCESSABLE_ENTITY:
			if (isTwoFactorSelfHandled) {
				break;
			}

			storeInstance.dispatch('toast/showToast', {
				header: 'Unprocessable Entity (422)',
				content: errorData?.message || 'Please verify your form data.',
				type: 'danger',
			});
			break;

		// 429 - Too Many Requests
		case HTTP_STATUS_CODE.TOO_MANY_REQUESTS:
			if (isTwoFactorSelfHandled) {
				break;
			}

			storeInstance.dispatch('toast/showToast', {
				header: 'Too Many Requests (429)',
				content: errorData?.message || 'You are sending too many requests. Please wait a moment.',
				type: 'danger',
			});
			break;

		// 5XX - Internal Server Error and other unexpected errors
		default:
			storeInstance.dispatch('toast/showToast', {
				header: `Internal Server Error (${statusCode})`,
				content: errorData?.message || 'Please try again or contact support.',
				type: 'danger',
			});
	}

	return Promise.reject(error);
};
